import React, { useState, useEffect, useMemo } from 'react';
import { TAutomationActionOption } from '@frontend/app/hooks';
import { AutomationNodeV2 } from '../../types';
import { Button } from '@/shadcn/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';

const UPDATE_MEMBER_DATA_TYPES = {
  ADD_TAG: 'ADD_TAG',
  ADD_TO_GROUP: 'ADD_TO_GROUP',
} as const;

const UPDATE_TYPE_LABELS = {
  [UPDATE_MEMBER_DATA_TYPES.ADD_TAG]: 'Add Tag',
  [UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP]: 'Add to Group',
} as const;

interface UpdateMemberDataActionFormProps {
  selectedNode: AutomationNodeV2;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  actionOptions: TAutomationActionOption[];
  onSave: () => void;
  onCancel: () => void;
}

export const UpdateMemberDataActionForm: React.FC<UpdateMemberDataActionFormProps> = ({
  selectedNode,
  onUpdateNode,
  actionOptions,
  onSave,
  onCancel,
}) => {
  const [updateType, setUpdateType] = useState<string>('');
  const [fieldValue, setFieldValue] = useState<string>('');
  const [updateTypeValidationError, setUpdateTypeValidationError] = useState<string>('');
  const [fieldValueValidationError, setFieldValueValidationError] = useState<string>('');

  const updateMemberDataOption = useMemo(() => {
    return actionOptions?.find((option) => option.type === 'UPDATE_MEMBER_DATA');
  }, [actionOptions]);

  const tags = useMemo(() => updateMemberDataOption?.tags || [], [updateMemberDataOption]);
  const groups = useMemo(() => updateMemberDataOption?.groups || [], [updateMemberDataOption]);
  const updateTypes = useMemo(() => updateMemberDataOption?.updateTypes || [], [updateMemberDataOption]);

  // Initialize state from selectedNode metadata
  useEffect(() => {
    if (selectedNode && selectedNode.type === 'ACTION' && selectedNode.metadata) {
      // Map saved metadata back to form state
      const savedType = selectedNode.metadata.type;
      const savedFieldValue = selectedNode.metadata.fieldValue;

      // Convert saved 'TAG'/'GROUP' back to 'ADD_TAG'/'ADD_TO_GROUP'
      if (savedType === 'TAG') {
        setUpdateType(UPDATE_MEMBER_DATA_TYPES.ADD_TAG);
      } else if (savedType === 'GROUP') {
        setUpdateType(UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP);
      } else {
        setUpdateType('');
      }

      setFieldValue(savedFieldValue || '');
    }
  }, [selectedNode]);

  const validateForm = () => {
    const noUpdateTypeError = 'Please select an update type';
    const noFieldValueError = 'Please select a value';
    const noError = '';

    const updateTypeError = !updateType ? noUpdateTypeError : noError;
    const fieldValueError = !fieldValue ? noFieldValueError : noError;

    setUpdateTypeValidationError(updateTypeError);
    setFieldValueValidationError(fieldValueError);

    return updateTypeError === noError && fieldValueError === noError;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const updatedMetadata = {
      subType: 'UPDATE_MEMBER_DATA',
      resourceId: selectedNode.metadata.resourceId || 1, // Required by backend validation
      fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false, // Required by backend validation
      type: updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG ? 'TAG' : 'GROUP',
      fieldValue,
    };

    // Update the node metadata with the form values
    onUpdateNode(selectedNode.id, {
      metadata: updatedMetadata,
    });

    onSave();
  };

  const handleUpdateTypeChange = (value: string) => {
    setUpdateType(value);
    setFieldValue(''); // Reset field value when update type changes
    setFieldValueValidationError('');
  };

  const getFieldValueOptions = () => {
    if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      return tags;
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      return groups;
    }
    return [];
  };

  const getFieldValuePlaceholder = () => {
    if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG) {
      return 'Select a tag';
    } else if (updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TO_GROUP) {
      return 'Select a group';
    }
    return 'Select an option';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Scrollable content area with bottom padding for buttons */}
      <div className="flex-1 overflow-y-auto space-y-4 px-1 pb-20">
        {/* Update Type section */}
        <div className="space-y-2">
          <Label htmlFor="update-type">Update Type</Label>
          <Select
            value={updateType}
            onValueChange={handleUpdateTypeChange}
          >
            <SelectTrigger id="update-type">
              <SelectValue placeholder="Select update type" />
            </SelectTrigger>
            <SelectContent>
              {updateTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {UPDATE_TYPE_LABELS[type as keyof typeof UPDATE_TYPE_LABELS] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {updateTypeValidationError && (
            <p className="text-sm text-red-500">{updateTypeValidationError}</p>
          )}
        </div>

        {/* Field Value section - only show if update type is selected */}
        {updateType && (
          <div className="space-y-2">
            <Label htmlFor="field-value">
              {updateType === UPDATE_MEMBER_DATA_TYPES.ADD_TAG ? 'Tag' : 'Group'}
            </Label>
            <Select
              value={fieldValue}
              onValueChange={setFieldValue}
            >
              <SelectTrigger id="field-value">
                <SelectValue placeholder={getFieldValuePlaceholder()} />
              </SelectTrigger>
              <SelectContent>
                {getFieldValueOptions().map((option) => (
                  <SelectItem key={option.value} value={option.displayName}>
                    {option.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldValueValidationError && (
              <p className="text-sm text-red-500">{fieldValueValidationError}</p>
            )}
          </div>
        )}
      </div>

      {/* Action buttons - Fixed at bottom */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-start gap-2 p-4 border-t bg-white shadow-lg">
        <Button
          variant="outline"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
        >
          Done
        </Button>
      </div>
    </div>
  );
};
